import { getApiBaseUrl } from '../utils/environment.mjs';
import React, {useRef} from "react";
import useInvoiceUpload from "../expenseReimbursement/hook/useInvoiceUpload.mjs";
import { useNavigate } from "react-router-dom";
import axios from 'axios';
import { useDialog } from '../components/dialog/DialogProvider.mjs';


// `readingCount` is how many of the rows this uploader created are still being
// read. The embedding form owns that state (each row reports its own status);
// it is only ever above 0 where extraction is enabled for the claim's owner.
const InvoiceUploader = ({ onExtracted = null, claimId = null, readingCount = 0 }) => {
  // Custom in-app dialogs replacing window.confirm/window.alert. `alert` is
  // named to shadow the global deliberately: every existing alert(...) call in
  // this file then routes through the styled modal with no other change.
  const { alert } = useDialog();
  const fileInputRef = useRef(null);
  const embedded = typeof onExtracted === 'function';
  const {
    files,
    isUploading,
    uploadProgress,
    isExtracting,
    extractionComplete,
    error,
    handleFileSelect,
    removeFile,
    uploadFiles,
    resetForm
  } = useInvoiceUpload(onExtracted, claimId);

  // Trigger file input click
  const openFileSelector = () => {
    fileInputRef.current.click();
  };

  const navigate = useNavigate();

  // Handle file input change
  const handleFileInputChange = (e) => {
    handleFileSelect(e.target.files);
    e.target.value = null;
  };



const proceedManually = async () => {
  try {
    const response = await axios.post(`${getApiBaseUrl()}/expenditure-claims`, {
      status: 'draft'
    }, {
      withCredentials: true
    });

    const claimId = response.data.id || response.data._id;
    navigate(`/claim/expense/${claimId}?mode=manual`);

  } catch (error) {
    console.error('Error creating expenditure claim:', error);

    if (error.response?.status === 401) {
      console.error('User not authenticated');
    } else {
      console.error('Error:', error.response?.data?.error || error.message);
      alert("Unable to use manual option, Please try uploading the invoices automatically")
    }
  }
};

  return (
    <div className="space-y-4">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      <div className="space-y-4">
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileInputChange}
          // Extensions AND MIME types: with ".pdf" alone iOS offered no Photos
          // or camera option, and listing image/jpeg lets it hand over a
          // JPEG in place of a HEIC photo.
          accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
          multiple
          className="hidden"
        />

        <div
          className="border-2 border-dashed border-gray-300 rounded-xl p-6 sm:p-8 text-center cursor-pointer hover:border-stanford-red hover:bg-gray-50 transition-colors"
          onClick={openFileSelector}
          onDragOver={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onDrop={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleFileSelect(e.dataTransfer.files);
          }}
        >
          <p className="text-gray-700 mb-2">Drag & drop your invoices here or click to browse</p>
          <p className="text-sm text-gray-500">Up to 10 files per batch, PDF, JPG or PNG{embedded ? ' - upload more batches to add extra line items' : ''}</p>
        </div>

        {/* Reading happens after the upload, one row at a time, so the rows
            are already in the table below. Say so, so nobody waits here. */}
        {readingCount > 0 && !isUploading && !isExtracting && (
          <div className="flex items-center gap-3 bg-gray-50 rounded-xl p-4" role="status" aria-live="polite">
            <div className="w-5 h-5 border-2 border-stanford-red border-t-transparent rounded-full animate-spin flex-shrink-0"></div>
            <div>
              <div className="text-sm font-medium text-gray-800">
                Reading invoices… {readingCount} still to read
              </div>
              <div className="text-xs text-gray-500">
                Each line fills in as its invoice is read. Only empty fields are filled - check every value, and add what was bought and why yourself.
              </div>
            </div>
          </div>
        )}

        {files.length > 0 && !isUploading && !isExtracting && !extractionComplete && (
          <div className="bg-gray-50 rounded-xl p-4">
            <h3 className="font-semibold text-gray-800 mb-3">Selected Files ({files.length}/10)</h3>
            <ul className="space-y-2">
              {files.map((file, index) => (
                <li key={index} className="flex items-center gap-3 bg-white p-3 rounded-lg border border-gray-200">
                  <div className="hidden sm:block text-xl text-red-500"></div>
                  <div className="flex-1 min-w-0">
                    <span className="text-sm text-gray-800 truncate block">{file.name}</span>
                  </div>
                  <div className="text-xs text-gray-500 whitespace-nowrap">{(file.size / 1024 / 1024).toFixed(2)} MB</div>
                  <button
                    className="min-w-[44px] min-h-[44px] -my-2 -mr-2 sm:min-w-0 sm:min-h-0 sm:my-0 sm:mr-0 p-1 text-gray-400 hover:text-red-500 transition-colors"
                    onClick={() => removeFile(index)}
                    disabled={isUploading}
                    aria-label={`Remove ${file.name}`}
                  >
                    X
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {(isUploading || isExtracting) && (
          <div className="bg-gray-50 rounded-xl p-4">
            {isExtracting ? (
              <>
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-5 h-5 border-2 border-green-500 border-t-transparent rounded-full animate-spin flex-shrink-0"></div>
                  <div>
                    <div className="text-sm font-medium text-gray-800">Creating line items…</div>
                    <div className="text-xs text-gray-500">Adding one line per invoice with the document attached. You then fill in the details.</div>
                  </div>
                </div>
                {/* Indeterminate progress - extraction has no percentage. */}
                <div className="w-full bg-gray-200 rounded-full h-1.5 overflow-hidden">
                  <div className="h-full w-1/3 bg-green-500 rounded-full animate-indeterminate"></div>
                </div>
              </>
            ) : (
              <>
                <div className="w-full bg-gray-200 rounded-full h-2 mb-2 overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-300 bg-stanford-red"
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
                <div className="text-sm text-center text-gray-600">
                  {uploadProgress.toFixed(0)}% Uploaded
                </div>
              </>
            )}
          </div>
        )}

        {extractionComplete && (
          <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
            <div className="text-3xl text-green-500 mb-2">Done</div>
            <p className="text-green-700 font-medium">Line items created - fill in the details below.</p>
          </div>
        )}

        {/* Primary action is first in the DOM, so a plain column keeps it on
            top on phones; from sm up this is the original wrapping row. */}
        <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3">
          <button
            className="px-6 py-3 bg-stanford-red text-white rounded-lg hover:bg-stanford-light-red transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={uploadFiles}
            disabled={files.length === 0 || isUploading || isExtracting || extractionComplete}
          >
            {isUploading ? 'Uploading...' : isExtracting ? 'Creating lines...' : extractionComplete ? 'Complete!' : 'Upload Invoices'}
          </button>

          <button
            className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={resetForm}
            disabled={isUploading || isExtracting || extractionComplete}
          >
            Reset
          </button>
          {!embedded && (
            <button
              className="px-6 py-3 border border-stanford-red text-stanford-red rounded-lg hover:bg-stanford-red hover:text-white transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={proceedManually}
              disabled={isUploading || isExtracting || extractionComplete}
            >
              Proceed Manually
            </button>
          )}
        </div>
      </div>
    </div>
  );
};



export default InvoiceUploader;
